import { DateRangeSelector } from '@app/components/date-range-selector/DateRangeSelector';
import type {
  DateRangePresetId,
  DateRangeValue,
} from '@app/components/date-range-selector/date-range-presets';
import {
  getPresetRange,
  isCustomPreset,
} from '@app/components/date-range-selector/date-range-presets';
import { useTimezone } from '@app/core/hooks/useTimezone';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { GenericParser, useQueryState } from 'nuqs';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useListViewContext } from '../context/ListViewContext';
import type { DateRangeFilterValue } from '../helpers/parse-as-date-range-filter';

/**
 * Props for the generic date range filter component.
 */
export interface GenericDateRangeFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label (shown above the selector). Pass false to hide the label. */
  label: MessageDescriptor | false;
  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). */
  parser: GenericParser<DateRangeFilterValue | null>;
  /** Whether the filter is active (addable filters). Undefined for permanent filters. */
  isActive?: boolean;
  /** Callback when filter is cleared (addable filters). */
  onRemove?: () => void;
  /** Custom styles for the container */
  sx?: SystemStyleObject<Theme>;
}

/**
 * Derives DateRangeValue + presetId only for DateRangeSelector UI (trigger label and custom dialogs).
 * The filter value remains preset-based; we do not convert date range to preset.
 */
function presetValueToSelectorState(
  urlValue: DateRangeFilterValue | null,
  timezone: string,
): { value: DateRangeValue | null; presetId: DateRangePresetId | null } {
  if (urlValue == null) {
    return { value: null, presetId: null };
  }

  const { presetId, from, to } = urlValue;
  if (isCustomPreset(presetId) && from != null && to != null) {
    return {
      value: { from: new Date(from), to: new Date(to) },
      presetId,
    };
  }

  const range = getPresetRange(presetId, timezone);
  return { value: range, presetId };
}

/**
 * Generic date range filter. The filter value is the preset (presetId; for custom presets also from/to).
 * Uses DateRangeSelector for UI only; list/API receives the preset, not a converted date range.
 */
export function GenericDateRangeFilter({
  name,
  label,
  parser,
  isActive,
  onRemove,
  sx,
}: GenericDateRangeFilterProps) {
  const { $t } = useIntl();
  const tz = useTimezone();
  const { setFilterValue } = useListViewContext();

  const [urlValue] = useQueryState(name, parser) as [DateRangeFilterValue | null, never];
  const { value, presetId } = presetValueToSelectorState(urlValue, tz);

  const handleChange = (range: DateRangeValue, newPresetId: DateRangePresetId) => {
    // Filter value is the preset (and for custom presets, from/to as well)
    const next: DateRangeFilterValue = {
      presetId: newPresetId,
      ...(isCustomPreset(newPresetId) && {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      }),
    };
    setFilterValue(name, next);
  };

  const handleClear = () => {
    setFilterValue(name, null);
  };

  if (isActive === false && !urlValue) {
    return null;
  }

  const hasValue = urlValue != null;
  // Inner clear only for addable filters (onRemove is set). Permanent filters (e.g. "ÚLTIMOS 30 DÍAS") do not show clear.
  const showClear = hasValue && onRemove != null;

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          minWidth: '18ch',
        },
        sx ?? {},
      ]}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {label !== false && (
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {$t(label)}
          </Box>
        )}
        <DateRangeSelector
          value={value ?? undefined}
          presetId={presetId ?? undefined}
          onChange={handleChange}
          buttonProps={{
            variant: 'outlined',
            color: 'secondary',
            sx: { width: '100%', backgroundColor: 'white', minHeight: '40px' },
          }}
        />
      </Box>
      {showClear && (
        <IconButton
          aria-label={$t({
            id: 'common.clear',
            defaultMessage: 'Clear',
          })}
          onClick={handleClear}
          size="small"
          disabled={!hasValue}
          sx={{
            mt: label === false ? 0.75 : 2.25,
            flexShrink: 0,
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
